import React, { useEffect, useMemo, useState } from "react";
import {
  FormCheck,
  FormControl,
  FormLabel,
  FormSelect,
  OverlayTrigger,
  Placeholder,
  Tab,
  Tabs,
  Toast,
  ToastContainer,
  Tooltip,
} from "react-bootstrap";
import { Modal } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { useSession } from "../../contexts/SessionContext";
import { Evaluation } from "../../models/Evaluation";
import { PermissionComponent, validateUserPermission } from "../../services/PermissionGuard";
import ShortcutService from "../../services/ShortcutService";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import DstButton from "../DstButton/DstButton";
import DstConfirm from "../DstConfirm/DstConfirm";
import Chevron from "../DstIcons/Chevron";
import Edit from "../DstIcons/Edit";
import Key from "../DstIcons/Key";
import LogIn from "../DstIcons/LogIn";
import LogOut from "../DstIcons/LogOut";
import Hubspot from "../DstIcons/hubspot";
import MultiSelectDropdown from "../MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../Popup/Popup";
import { ReportParams, UserBlockProps } from "./UserBlock.d";
import {
  canGenerateStudentCard,
  fetchEvaluations,
  fetchProgressReport,
  freeProxmoxLabs,
  generateStudentCard,
  getLatestStudentCardDate,
  handleSaveEvaluations,
  inviteUserToEvaluation,
  mergeUser,
  refreshUserEvaluation,
  regenerateStudentCard,
  resendCredentials,
} from "./UserBlock.function";
import "./UserBlock.scss";
import ProxmoxDeployModal from "./components/ProxmoxDeployModal";

const _renderEvaluationsModal = (
  buttonClicked: boolean,
  setButtonClicked: Function,
  showEvaluationsModal: boolean,
  toggleEvaluationsModal: Function,
  evaluations: Evaluation[],
  user: any,
  setSelectedEvaluations: Function,
  selectedEvaluations: Evaluation[],
  setShowEvaluationsModal: Function,
  setEvaluations: Function,
  refreshUserCertifications: Function,
  language: string
) => {
  const modalSelectedTitle =
    selectedEvaluations.length > 0
      ? selectedEvaluations.length === 1
        ? `${selectedEvaluations[0].name}`
        : selectedEvaluations.length === 2
        ? `${selectedEvaluations[0].name} & ${translate(language, "PAGES.USER.USER_BLOCK.OTHER")}`
        : `${selectedEvaluations[0].name} & ${selectedEvaluations.length - 1} ${translate(
            language,
            "PAGES.USER.USER_BLOCK.OTHERS"
          )}`
      : translate(language, "PAGES.USER.USER_BLOCK.SELECT_EVAL");

  return (
    <Modal
      show={showEvaluationsModal}
      onHide={() => {
        toggleEvaluationsModal();
        setSelectedEvaluations([]);
      }}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{translate(language, "PAGES.USER.USER_BLOCK.EVALUATIONS")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <MultiSelectDropdown
          badgeItemProperty="description"
          displayItem={(evaluation: any) => (
            <span>
              {evaluation.description} <span className="dark-gray-font">({evaluation.s3_name})</span>
            </span>
          )}
          label={modalSelectedTitle}
          toggleClass="text-truncate w-100"
          onConfirm={setSelectedEvaluations as (selectedItems: any[]) => void}
          items={evaluations}
          language={language}
        />
      </Modal.Body>
      <Modal.Footer>
        <DstButton
          variant="primary"
          value={translate(language, "PAGES.USER.USER_BLOCK.SAVE")}
          clickFunction={() => {
            handleSaveEvaluations(
              user,
              selectedEvaluations,
              setButtonClicked,
              evaluations,
              setEvaluations,
              setShowEvaluationsModal,
              setSelectedEvaluations,
              refreshUserEvaluation,
              refreshUserCertifications,
              language
            );
          }}
          disabled={!selectedEvaluations.length || buttonClicked}
        />
      </Modal.Footer>
    </Modal>
  );
};

const _renderInviteEvaluationModal = (
  buttonClicked: boolean,
  setButtonClicked: Function,
  showInviteEvaluationsModal: boolean,
  toggleInviteEvaluationsModal: Function,
  evaluations: Evaluation[],
  user: any,
  setSelectedEvaluations: Function,
  selectedEvaluations: Evaluation[],
  setShowInviteEvaluationsModal: Function,
  language: string,
  scheduledDate: string,
  setScheduledDate: Function,
  showAllEvaluations: boolean,
  setShowAllEvaluations: React.Dispatch<React.SetStateAction<boolean>>,
  refreshUserExams: Function
) => {
  const modalSelectedTitle =
    selectedEvaluations.length > 0
      ? selectedEvaluations.length === 1
        ? `${selectedEvaluations[0].name}`
        : selectedEvaluations.length === 2
        ? `${selectedEvaluations[0].name} & ${translate(language, "PAGES.USER.USER_BLOCK.OTHER")}`
        : `${selectedEvaluations[0].name} & ${selectedEvaluations.length - 1} ${translate(
            language,
            "PAGES.USER.USER_BLOCK.OTHERS"
          )}`
      : translate(language, "PAGES.USER.USER_BLOCK.INVITE_EVAL.SELECT_EVAL");

  return (
    <Modal
      show={showInviteEvaluationsModal}
      onHide={() => {
        toggleInviteEvaluationsModal();
        setSelectedEvaluations([]);
        setScheduledDate("");
      }}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{translate(language, "PAGES.USER.USER_BLOCK.INVITE_EVAL.MODAL.TITLE")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <MultiSelectDropdown
          badgeItemProperty="description"
          displayItem={(evaluation: any) => (
            <span>
              {evaluation.description} <span className="dark-gray-font">({evaluation.s3_name})</span>
            </span>
          )}
          label={modalSelectedTitle}
          toggleClass="text-truncate w-100 m-regular"
          onConfirm={setSelectedEvaluations as (selectedItems: any[]) => void}
          items={evaluations}
          language={language}
          isSingleSelect
          borderToggle
        />
        <div className="mt-3 m-regular">
          <label htmlFor="scheduledDate">
            {translate(language, "PAGES.USER.USER_BLOCK.INVITE_EVAL.SCHEDULE_DATE")}
          </label>
          <input
            type="datetime-local"
            id="scheduledDate"
            className="form-control"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            min={new Date().toISOString().slice(0, 16)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between align-items-center w-100">
          <FormCheck
            type="checkbox"
            id="show-all-evaluations"
            label={
              <label className="m-regular" htmlFor="show-all-evaluations">
                {translate(language, "PAGES.USER.USER_BLOCK.SHOW_ALL_EVALUATIONS")}
              </label>
            }
            checked={showAllEvaluations}
            onChange={(event) => {
              setShowAllEvaluations(event.target.checked);
            }}
          />
          <DstButton
            variant="primary"
            value={translate(language, "PAGES.USER.USER_BLOCK.INVITE_EVAL.MODAL.CONFIRM")}
            clickFunction={() => {
              setButtonClicked(true);
              selectedEvaluations.forEach((evaluation) => {
                inviteUserToEvaluation(user.id, evaluation.id, language, refreshUserExams, scheduledDate || undefined);
              });
              setSelectedEvaluations([]);
              setShowInviteEvaluationsModal(false);
              setButtonClicked(false);
            }}
            disabled={!selectedEvaluations.length || buttonClicked}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const UserBlock = ({
  edit,
  editFunction,
  language,
  loadedDocuments = true,
  loginAsFunction,
  logoutFunction,
  mock,
  refreshUserCertifications,
  refreshUserDocuments,
  refreshUserExams,
  user,
}: UserBlockProps) => {
  const navigate = useNavigate();
  const { permissions, showAlert } = useSession();
  const canReadEvaluations = validateUserPermission({ methods: ["GET"], route: "/evaluations" }, permissions);
  const canReadUserDocuments = validateUserPermission({ methods: ["GET"], route: "/users/*/documents" }, permissions);
  const [showCopyUserId, setShowCopyUserId] = useState(false);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [showEvaluationsModal, setShowEvaluationsModal] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingReportExternal, setLoadingReportExternal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showReportExternalModal, setShowReportExternalModal] = useState(false);
  const [reportParams, setReportParams] = useState<ReportParams>({
    sections: ["modules_exams", "certifications", "project_info", "attendance", "log_time"],
  });
  const [reportExternalCohortId, setReportExternalCohortId] = useState<number | undefined>();
  const [showPopup, setShowPopup] = useState(false);
  const toggleEvaluationsModal = () => setShowEvaluationsModal(!showEvaluationsModal);
  const [selectedEvaluations, setSelectedEvaluations] = useState<Evaluation[]>([]);
  const [loadedCertif, setLoadedCertif] = useState(false);
  const [buttonClicked, setButtonClicked] = useState(false);
  const [showInviteEvaluationModal, setShowInviteEvaluationModal] = useState(false);
  const toggleInviteEvaluationModal = () => setShowInviteEvaluationModal(!showInviteEvaluationModal);
  const [scheduledDate, setScheduledDate] = useState<string>("");
  const [showMergeUserModal, setShowMergeUserModal] = useState(false);
  const [mergeTargetUser, setMergeTargetUser] = useState<any>(null);
  const [showConfirmMerge, setShowConfirmMerge] = useState(false);
  const [loadingMerge, setLoadingMerge] = useState(false);
  const [loadingResendCredentials, setLoadingResendCredentials] = useState(false);
  const [showConfirmResendCredentials, setShowConfirmResendCredentials] = useState(false);
  const [showResendCredentialsToast, setShowResendCredentialsToast] = useState(false);
  const [showCopyShortcutToast, setShowCopyShortcutToast] = useState(false);
  const [studentCardSuccessToast, setStudentCardSuccessToast] = useState<"generate" | "regenerate" | null>(null);
  const [showAllEvaluations, setShowAllEvaluations] = useState(false);
  const [showProxmoxModal, setShowProxmoxModal] = useState(false);
  const [proxmoxDeployedCount, setProxmoxDeployedCount] = useState<number | null>(null);
  const [showConfirmFreeProxmoxLabs, setShowConfirmFreeProxmoxLabs] = useState(false);
  const [loadingFreeProxmoxLabs, setLoadingFreeProxmoxLabs] = useState(false);
  const [freeProxmoxLabsCount, setFreeProxmoxLabsCount] = useState<number | null>(null);
  const [showModalStudentCard, setShowModalStudentCard] = useState(false);
  const [studentCardBirthdate, setStudentCardBirthdate] = useState("");
  const [studentCardImage, setStudentCardImage] = useState<File | null>(null);
  const [useProfilePictureForStudentCard, setUseProfilePictureForStudentCard] = useState(true);
  const [regenerateStudentCardPictureSource, setRegenerateStudentCardPictureSource] = useState<
    "previous" | "profile" | "upload"
  >("previous");
  const [loadingStudentCard, setLoadingStudentCard] = useState(false);
  const studentCardPreviewUrl = useMemo(
    () => (studentCardImage ? URL.createObjectURL(studentCardImage) : null),
    [studentCardImage]
  );
  const studentCardSelectedPreviewUrl = useProfilePictureForStudentCard ? user.image_url : studentCardPreviewUrl;
  const regenerateStudentCardSelectedPreviewUrl =
    regenerateStudentCardPictureSource === "profile"
      ? user.image_url
      : regenerateStudentCardPictureSource === "upload"
      ? studentCardPreviewUrl
      : null;

  const canGenerateStudentCardFlag = useMemo(() => {
    if (!canReadUserDocuments) return true;

    const latestStudentCardDate = getLatestStudentCardDate(user?.user_documents ?? []);
    return canGenerateStudentCard(!!latestStudentCardDate, latestStudentCardDate);
  }, [canReadUserDocuments, user?.user_documents]);
  const mergeSelectedUser = (userId: number) => {
    if (!userId) {
      alert(translate(language, "PAGES.USER.USER_BLOCK.MERGE.SELECT_VALID_USER"));
      return;
    }

    mergeUser(user.id, userId, language, () => {
      setShowMergeUserModal(false);
      setMergeTargetUser(null);
      navigate(`/user/${userId}`);
    });
  };

  const copyUserIdToClipboardAndShowTooltip = () => {
    navigator.clipboard.writeText(user.id).then(() => {
      setShowCopyUserId(true);
      setTimeout(() => setShowCopyUserId(false), 3000);
    });
  };

  useEffect(() => {
    if (!validateUserPermission({ methods: ["GET"], route: "/auth/login-as/*" }, permissions)) return;

    const listen_destructor = ShortcutService.listen("l", () => loginAsFunction?.(), false, true);

    return () => {
      listen_destructor();
    };
  }, [loginAsFunction, permissions]);

  useEffect(() => {
    if (canReadEvaluations) {
      fetchEvaluations(setEvaluations, setLoadedCertif);
    }
  }, [canReadEvaluations]);

  useEffect(() => {
    const copyUserToClipboard = () => {
      if (!user.first_name && !user.last_name) return;
      const cohorts = user.user_cohorts?.map((cohort: any) => cohort.name).join(" / ") ?? "";
      const userToCopy = `${user.first_name} ${user.last_name} ( ${cohorts} )  ${window.location.href}`;
      navigator.clipboard
        .writeText(userToCopy)
        .then(() => setShowCopyShortcutToast(true))
        .catch((err) => console.error("Failed to copy to clipboard", err));
    };
    const listen_destructor = ShortcutService.listen("c", copyUserToClipboard, false, true);
    return () => listen_destructor();
  }, [user.first_name, user.last_name, user.user_cohorts]);

  const filteredEvaluations = useMemo(() => {
    if (showAllEvaluations) return evaluations;
    return evaluations.filter(
      (evaluation) => !new Set(user.user_certifs?.map((cert: any) => cert.certification_name)).has(evaluation.name)
    );
  }, [evaluations, user.user_certifs, showAllEvaluations]);

  const userIcons = user?.user_flags
    ?.filter(
      (flag: any, index: number, flags: any[]) =>
        flags.findIndex((otherFlag: any) => otherFlag.name === flag.name) === index
    )
    .map((flag: any, index: number) => (
      <OverlayTrigger
        key={`overlay-trigger-${index}`}
        placement="bottom"
        overlay={
          <Tooltip id={`tooltip-${flag.name.toLowerCase().replace(/\s+/g, "-")}`}>
            {flag.name}
            {user.user_flags
              .filter((sameFlag: any) => sameFlag.name === flag.name && sameFlag.cohort)
              .map((sameFlag: any) => (
                <div key={sameFlag.cohort.id}>
                  {sameFlag.cohort.name} (#{sameFlag.cohort.id})
                </div>
              ))}
          </Tooltip>
        }
      >
        <span className={`dst-icon-${flag.icon_name} px-2 white-background`} aria-label={flag.description}></span>
      </OverlayTrigger>
    ));

  const hasApprenticeshipFlag = user?.user_flags?.some((flag: any) => flag.name === "Apprenticeship");

  const transactions = user.transactions ? JSON.parse(user.transactions) : [];

  useEffect(() => {
    if (user.user_cohorts?.length === 1) {
      setReportParams((params) => ({ ...params, cohortId: user.user_cohorts[0].id }));
    }
  }, [user.user_cohorts]);

  useEffect(() => {
    return () => {
      if (studentCardPreviewUrl) {
        URL.revokeObjectURL(studentCardPreviewUrl);
      }
    };
  }, [studentCardPreviewUrl]);

  const _isSectionEnabled = (section: string) => reportParams.sections?.includes(section) ?? false;
  const _isLogTimeFormatsEnabled = (logTimeFormat: string) =>
    reportParams.logTimeFormats?.includes(logTimeFormat) ?? false;

  const _toggleSelectedReportSection = (section: string) => {
    const currentSections = reportParams.sections ?? [];
    setReportParams((params) => ({
      ...params,
      sections: currentSections.includes(section)
        ? currentSections.filter((selectedSection) => selectedSection !== section)
        : [...currentSections, section],
    }));
  };

  const _toggleSelectedReportLogTimeFormats = (logTimeFormat: string) => {
    const currentLogTimeFormats = reportParams.logTimeFormats ?? [];
    setReportParams((params) => ({
      ...params,
      logTimeFormats: currentLogTimeFormats.includes(logTimeFormat)
        ? currentLogTimeFormats.filter((selectedFormat) => selectedFormat !== logTimeFormat)
        : [...currentLogTimeFormats, logTimeFormat],
    }));
  };

  return (
    <div className="user-profile-container white-background border rounded position-relative">
      <div className="semi-circle position-absolute"></div>
      <div className="p-3 user-profile position-relative z-1">
        <PermissionComponent neededPermission={[{ methods: ["PATCH"], route: "/users/*" }]}>
          <div className="s-regular text-end blue-font clickable" onClick={editFunction}>
            <div style={{ visibility: edit && !mock ? "visible" : "hidden" }}>
              <Edit classes="me-1" />
              <u>{translate(language, "PAGES.USER.USER_BLOCK.EDIT")}</u>
            </div>
          </div>
        </PermissionComponent>
        <div className="user-profile-header text-center mt-4">
          <div className="d-flex justify-content-center">
            <div className="position-relative w-fit">
              {!mock ? (
                <img
                  src={user.image_url || "/assets/images/user.jpg"}
                  alt="user mock"
                  className="rounded-circle object-fit-cover"
                  height="60px"
                  width="60px"
                />
              ) : (
                <Placeholder animation="glow">
                  <Placeholder as="img" height={60} width={60} style={{ borderRadius: 60 }} />
                </Placeholder>
              )}
              {!mock ? (
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip id="start-end-date-tooltip">
                      {formatDateHumanly(language, user.dt_start)} - {formatDateHumanly(language, user.dt_end)}
                    </Tooltip>
                  }
                >
                  <span
                    className={`position-absolute bottom-0 start-100 translate-middle-x badge border border-light rounded-circle ${
                      user.is_active ? "aqua" : "red"
                    }-background p-2`}
                  >
                    <span className="visually-hidden">
                      {translate(language, `PAGES.USER.USER_BLOCK.${user.is_active ? "ACTIVE" : "INACTIVE"}_STATUS`)}
                    </span>
                  </span>
                </OverlayTrigger>
              ) : (
                <React.Fragment></React.Fragment>
              )}
              {!mock ? (
                <OverlayTrigger placement="bottom" overlay={<Tooltip>{user.role.name}</Tooltip>}>
                  <div className="position-absolute bottom-0 start-0 translate-middle-x badge border border-light rounded-circle gray-background p-2">
                    <span className="dst-icon-shield-tick position-absolute top-50 start-50 translate-middle" />
                  </div>
                </OverlayTrigger>
              ) : (
                <React.Fragment></React.Fragment>
              )}
            </div>
          </div>
          {!mock ? (
            <React.Fragment>
              <h2 className="mb-0 mt-3">
                {user.first_name} {user.last_name} 
                <ReactCountryFlag
                  svg
                  countryCode={user.language.split("-")[1]}
                  title={user.language}
                  className="pb-1"
                />
              </h2>
              <p className="s-regular silver-gray-font">{user.email}</p>
              <div className="h2 position-relative d-flex justify-content-center align-items-center mb-4">
                <hr className="line-icons position-absolute my-3" />
                <div className="d-flex flex-wrap justify-content-center align-content-center blue-font w-100 z-1">
                  {userIcons}
                  {user.has_extra_time && (
                    <OverlayTrigger
                      key="overlay-trigger-clock"
                      placement="bottom"
                      overlay={
                        <Tooltip id="tooltip-extra-time" aria-label="Extra time">
                          {translate(language, "PAGES.USER.USER_BLOCK.EXTRA_TIME")}
                        </Tooltip>
                      }
                    >
                      <span className="dst-icon-clock-stopwatch px-2 white-background" aria-hidden="true"></span>
                    </OverlayTrigger>
                  )}
                </div>
              </div>
            </React.Fragment>
          ) : (
            <Placeholder animation="glow">
              <div className="mb-0 mt-3">
                <Placeholder as="h2" xs={12} />
                <Placeholder as="p" xs={12} />
              </div>
            </Placeholder>
          )}
        </div>
        {!mock ? (
          <React.Fragment>
            <Tabs defaultActiveKey="home" id="user-tab" className="mb-3" justify>
              <Tab eventKey="home" title={translate(language, "PAGES.USER.USER_BLOCK.COHORT")}>
                <div className="user-cohorts">
                  {user.user_cohorts?.length ? (
                    user.user_cohorts?.map((user_cohort: any, index: number) => (
                      <Link
                        key={`cohort-${index}`}
                        to={`/cohort/${user_cohort.id}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <div className="d-flex mb-3">
                          <OverlayTrigger
                            placement="right"
                            overlay={
                              <Tooltip id={`status-tooltip-${index}`}>
                                {formatDateHumanly(language, user_cohort.dt_start)} -{" "}
                                {formatDateHumanly(language, user_cohort.dt_end)}
                              </Tooltip>
                            }
                          >
                            <div className="d-flex me-2">
                              <div
                                className={`user-cohort-${
                                  new Date() > new Date(user_cohort.dt_end) ? "inactive" : "active"
                                } vr`}
                              >
                                <span className="visually-hidden">
                                  {translate(
                                    language,
                                    `PAGES.USER.USER_BLOCK.${
                                      new Date() > new Date(user_cohort.dt_end) ? "INACTIVE" : "ACTIVE"
                                    }_STATUS`
                                  )}
                                </span>
                              </div>
                            </div>
                          </OverlayTrigger>
                          <div>
                            <div className="s-regular">{user_cohort.name}</div>
                            <div className="xxs-regular">
                              {user_cohort.cohort_leader.first_name} {user_cohort.cohort_leader.last_name}
                              {user_cohort.program_manager &&
                                ` - ${user_cohort.program_manager.first_name} ${user_cohort.program_manager.last_name}`}
                            </div>
                          </div>
                          <div className="align-self-center ms-4">
                            <Chevron />
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="xs-regular smoke-gray-font text-center">
                      {translate(language, "PAGES.USER.USER_BLOCK.NO_COHORT")}
                    </p>
                  )}
                </div>
              </Tab>
              <Tab eventKey="profile" title={translate(language, "PAGES.USER.USER_BLOCK.PROJECT")}>
                <div className="user-projects">
                  {user.user_projects?.length ? (
                    user.user_projects?.map((user_project: any, index: number) => (
                      <div
                        className="clickable d-flex mb-3"
                        key={`project-${index}`}
                        onClick={(event: any) => completeNavigate(event, navigate, `/project/${user_project.id}`)}
                      >
                        <div className="user-project-blue ps-2">
                          <div className="s-regular">{user_project.name}</div>
                          <div className="xxs-regular">
                            {user_project.description.length >= 120
                              ? `${user_project.description
                                  .slice(0, 120)
                                  .split(" ")
                                  .reverse()
                                  .slice(1)
                                  .reverse()
                                  .join(" ")}…`
                              : user_project.description}
                          </div>
                        </div>
                        <div className="align-self-center ms-4">
                          <Chevron />
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="xs-regular smoke-gray-font text-center">
                      {translate(language, "PAGES.USER.USER_BLOCK.NO_PROJECT")}
                    </p>
                  )}
                </div>
              </Tab>
            </Tabs>
            <div className="s-bold mt-4 mb-2">{translate(language, "PAGES.USER.USER_BLOCK.ACTION")}</div>
            <div className="d-flex flex-wrap row-gap-2">
              <OverlayTrigger
                show={showCopyUserId}
                placement="top"
                overlay={
                  <Tooltip id="tooltip-copied">
                    {translate(language, "PAGES.USER.USER_BLOCK.COPY_ID")}{" "}
                    <span className="dst-icon-check-done-02"></span>
                  </Tooltip>
                }
              >
                <div>
                  <DstButton
                    btnClass="me-2"
                    variant="secondary"
                    value={user.id}
                    btnImageBefore={<Key />}
                    btnSize="sm"
                    clickFunction={copyUserIdToClipboardAndShowTooltip}
                  />
                </div>
              </OverlayTrigger>
              <PermissionComponent neededPermission={{ methods: ["GET"], route: "/evaluations" }}>
                <React.Fragment>
                  <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/users/*/certifications" }]}>
                    {loadedCertif ? (
                      <React.Fragment>
                        <DstButton
                          variant="secondary"
                          value={translate(language, "PAGES.USER.USER_BLOCK.GIVE_CERTIF")}
                          btnImageBefore={<span className="dst-icon-trophy-01 pe-2 align-text-bottom" />}
                          btnSize="sm"
                          btnClass="me-2"
                          clickFunction={toggleEvaluationsModal}
                        />
                        {_renderEvaluationsModal(
                          buttonClicked,
                          setButtonClicked,
                          showEvaluationsModal,
                          toggleEvaluationsModal,
                          filteredEvaluations,
                          user,
                          setSelectedEvaluations,
                          selectedEvaluations,
                          setShowEvaluationsModal,
                          setEvaluations,
                          refreshUserCertifications,
                          language
                        )}
                      </React.Fragment>
                    ) : (
                      <DstButton mock btnClass="me-2" btnWidth="70px" />
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    neededPermission={[{ methods: ["POST"], route: "/users/*/evaluations/*/attempts" }]}
                  >
                    {loadedCertif ? (
                      <React.Fragment>
                        <DstButton
                          variant="secondary"
                          value={translate(language, "PAGES.USER.USER_BLOCK.INVITE_EVAL.BUTTON")}
                          btnImageBefore={<span className="dst-icon-pen-tool-plus pe-2 align-text-bottom" />}
                          btnSize="sm"
                          btnClass="me-2"
                          clickFunction={toggleInviteEvaluationModal}
                        />
                        {_renderInviteEvaluationModal(
                          buttonClicked,
                          setButtonClicked,
                          showInviteEvaluationModal,
                          toggleInviteEvaluationModal,
                          filteredEvaluations,
                          user,
                          setSelectedEvaluations,
                          selectedEvaluations,
                          setShowInviteEvaluationModal,
                          language,
                          scheduledDate,
                          setScheduledDate,
                          showAllEvaluations,
                          setShowAllEvaluations,
                          refreshUserExams
                        )}
                      </React.Fragment>
                    ) : (
                      <DstButton mock btnClass="me-2" btnWidth="140px" />
                    )}
                  </PermissionComponent>
                </React.Fragment>
              </PermissionComponent>
              {!!user.user_cohorts.length && (
                <React.Fragment>
                  <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/users/*/*/progress" }]}>
                    <React.Fragment>
                      <DstButton
                        variant="secondary"
                        value={translate(language, "PAGES.USER.USER_BLOCK.DOWNLOAD_REPORT")}
                        btnImageBefore={<span className="dst-icon-file-download-02 pe-2 align-text-bottom" />}
                        btnSize="sm"
                        btnClass="me-2"
                        clickFunction={() => setShowReportModal(true)}
                      />
                      <Popup
                        show={showReportModal}
                        onClose={() => setShowReportModal(false)}
                        modalTitle={translate(language, "PAGES.USER.USER_BLOCK.REPORT.POPUP_TITLE")}
                      >
                        <FormSelect
                          className="mb-2"
                          onChange={(e) => setReportParams((params) => ({ ...params, cohortId: +e.target.value }))}
                          value={reportParams.cohortId || ""}
                          disabled={!user.user_cohorts || user.user_cohorts?.length === 1}
                        >
                          <option value="" disabled>
                            {translate(language, "PAGES.USER.USER_BLOCK.REPORT.CHOOSE_COHORT")}
                          </option>
                          {user.user_cohorts?.map((cohort: any, index: number) => (
                            <option key={`report-cohort-choice-${index}`} value={cohort.id}>
                              {cohort.name}
                            </option>
                          ))}
                        </FormSelect>
                        <FormControl
                          type="text"
                          placeholder={translate(language, "PAGES.USER.USER_BLOCK.REPORT.CURSUS_NAME")}
                          className="mb-3"
                          onChange={(e) => setReportParams((params) => ({ ...params, cursusName: e.target.value }))}
                        />
                        <div className="mb-3">
                          <h6>{translate(language, "PAGES.USER.USER_BLOCK.REPORT.SELECT_SECTIONS")}</h6>
                          <FormCheck
                            id="modules-exams"
                            label={
                              <label htmlFor="modules-exams">
                                {translate(language, "PAGES.USER.USER_BLOCK.REPORT.SECTIONS.MODULES_EXAMS")}
                              </label>
                            }
                            checked={_isSectionEnabled("modules_exams")}
                            onChange={() => _toggleSelectedReportSection("modules_exams")}
                          />
                          <FormCheck
                            id="certifications"
                            label={
                              <label htmlFor="certifications">
                                {translate(language, "PAGES.USER.USER_BLOCK.REPORT.SECTIONS.CERTIFICATIONS")}
                              </label>
                            }
                            checked={_isSectionEnabled("certifications")}
                            onChange={() => _toggleSelectedReportSection("certifications")}
                          />
                          <FormCheck
                            id="project-info"
                            label={
                              <label htmlFor="project-info">
                                {translate(language, "PAGES.USER.USER_BLOCK.REPORT.SECTIONS.PROJECT_INFO")}
                              </label>
                            }
                            checked={_isSectionEnabled("project_info")}
                            onChange={() => _toggleSelectedReportSection("project_info")}
                          />
                          <FormCheck
                            id="attendance"
                            label={
                              <label htmlFor="attendance">
                                {translate(language, "PAGES.USER.USER_BLOCK.REPORT.SECTIONS.ATTENDANCE")}
                              </label>
                            }
                            checked={_isSectionEnabled("attendance")}
                            onChange={() => _toggleSelectedReportSection("attendance")}
                          />
                          <FormCheck
                            id="log-time"
                            label={
                              <label htmlFor="log-time">
                                {translate(language, "PAGES.USER.USER_BLOCK.REPORT.SECTIONS.LOG_TIME")}
                              </label>
                            }
                            checked={_isSectionEnabled("log_time")}
                            onChange={() => _toggleSelectedReportSection("log_time")}
                          />
                          {_isSectionEnabled("log_time") && (
                            <div className="mt-2 ms-3">
                              <FormCheck
                                id="log-time-daily"
                                label={
                                  <label htmlFor="log-time-daily">
                                    {translate(language, "PAGES.USER.USER_BLOCK.REPORT.SECTIONS.LOG_TIME_DAILY")}
                                  </label>
                                }
                                checked={_isLogTimeFormatsEnabled("daily")}
                                onChange={() => _toggleSelectedReportLogTimeFormats("daily")}
                              />
                              <FormCheck
                                id="log-time-monthly"
                                label={
                                  <label htmlFor="log-time-monthly">
                                    {translate(language, "PAGES.USER.USER_BLOCK.REPORT.SECTIONS.LOG_TIME_MONTHLY")}
                                  </label>
                                }
                                checked={_isLogTimeFormatsEnabled("monthly")}
                                onChange={() => _toggleSelectedReportLogTimeFormats("monthly")}
                              />
                              <FormCheck
                                id="include-meetings-logs"
                                label={
                                  <label htmlFor="include-meetings-logs">
                                    {translate(language, "PAGES.USER.USER_BLOCK.REPORT.SECTIONS.LOG_TIME_MEETINGS")}
                                  </label>
                                }
                                checked={reportParams.includeMeetingsLogs ?? false}
                                onChange={(event) =>
                                  setReportParams((params) => ({
                                    ...params,
                                    includeMeetingsLogs: event.target.checked,
                                  }))
                                }
                              />
                              <FormCheck
                                id="include-modules-estimated-time"
                                label={
                                  <label htmlFor="include-modules-estimated-time">
                                    {translate(
                                      language,
                                      "PAGES.USER.USER_BLOCK.REPORT.SECTIONS.MODULES_ESTIMATED_TIME"
                                    )}
                                  </label>
                                }
                                checked={reportParams.includeModulesEstimatedTime ?? false}
                                onChange={(event) =>
                                  setReportParams((params) => ({
                                    ...params,
                                    includeModulesEstimatedTime: event.target.checked,
                                  }))
                                }
                              />
                            </div>
                          )}
                        </div>
                        <DstButton
                          value={translate(language, "PAGES.USER.USER_BLOCK.REPORT.SUBMIT")}
                          btnClass="mx-auto d-block"
                          loading={loadingReport}
                          disabled={
                            !reportParams?.cohortId ||
                            loadingReport ||
                            !reportParams.sections.length ||
                            !user.user_cohorts?.length
                          }
                          clickFunction={() =>
                            fetchProgressReport(
                              language,
                              user.id,
                              { firstName: user.first_name, lastName: user.last_name },
                              reportParams,
                              setLoadingReport,
                              () => {
                                setReportParams({
                                  cohortId: user.user_cohorts?.length === 1 ? user.user_cohorts[0].id : undefined,
                                  sections: [
                                    "modules_exams",
                                    "certifications",
                                    "project_info",
                                    "attendance",
                                    "log_time",
                                  ],
                                });
                                setShowReportModal(false);
                              },
                              false
                            )
                          }
                        />
                      </Popup>
                    </React.Fragment>
                  </PermissionComponent>
                  <PermissionComponent
                    neededPermission={[{ methods: ["POST"], route: "/users/*/*/progress/external-format" }]}
                  >
                    <React.Fragment>
                      <DstButton
                        variant="secondary"
                        value={translate(language, "PAGES.USER.USER_BLOCK.DOWNLOAD_REPORT_EXTERNAL")}
                        btnImageBefore={<span className="dst-icon-file-download-03 pe-2 align-text-bottom" />}
                        btnSize="sm"
                        btnClass="me-2"
                        loading={loadingReportExternal}
                        disabled={loadingReportExternal}
                        clickFunction={() => {
                          if (user.user_cohorts?.length === 1) {
                            fetchProgressReport(
                              language,
                              user.id,
                              { firstName: user.first_name, lastName: user.last_name },
                              { cohortId: user.user_cohorts[0].id },
                              setLoadingReportExternal,
                              () => {},
                              true
                            );
                          } else {
                            setShowReportExternalModal(true);
                          }
                        }}
                      />
                      <Popup
                        show={showReportExternalModal}
                        onClose={() => setShowReportExternalModal(false)}
                        modalTitle={translate(language, "PAGES.USER.USER_BLOCK.REPORT_EXTERNAL.POPUP_TITLE")}
                      >
                        <FormSelect
                          className="mb-2"
                          onChange={(e) => setReportExternalCohortId(+e.target.value)}
                          value={reportExternalCohortId || ""}
                          disabled={!user.user_cohorts || user.user_cohorts?.length === 1}
                        >
                          <option value="" disabled>
                            {translate(language, "PAGES.USER.USER_BLOCK.REPORT_EXTERNAL.CHOOSE_COHORT")}
                          </option>
                          {user.user_cohorts?.map((cohort: any, index: number) => (
                            <option key={`report-cohort-choice-${index}`} value={cohort.id}>
                              {cohort.name}
                            </option>
                          ))}
                        </FormSelect>
                        <DstButton
                          value={translate(language, "PAGES.USER.USER_BLOCK.REPORT_EXTERNAL.SUBMIT")}
                          btnClass="mt-4 mx-auto d-block"
                          loading={loadingReportExternal}
                          disabled={!reportExternalCohortId || loadingReportExternal || !user.user_cohorts?.length}
                          clickFunction={() =>
                            fetchProgressReport(
                              language,
                              user.id,
                              { firstName: user.first_name, lastName: user.last_name },
                              { cohortId: reportExternalCohortId },
                              setLoadingReportExternal,
                              () => {
                                setReportExternalCohortId(undefined);
                                setShowReportExternalModal(false);
                              },
                              true
                            )
                          }
                        />
                      </Popup>
                    </React.Fragment>
                  </PermissionComponent>
                </React.Fragment>
              )}
              <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/proxmox/users" }]}>
                <React.Fragment>
                  <DstButton
                    variant="secondary"
                    value={translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.BUTTON")}
                    btnImageBefore={<span className="dst-icon-server-04 me-2 align-text-bottom" />}
                    btnSize="sm"
                    btnClass="me-2"
                    clickFunction={() => setShowProxmoxModal(true)}
                  />
                  <ProxmoxDeployModal
                    show={showProxmoxModal}
                    onHide={() => setShowProxmoxModal(false)}
                    onSuccess={(deployedUsersCount) => {
                      setShowProxmoxModal(false);
                      setProxmoxDeployedCount(deployedUsersCount);
                    }}
                    userIds={[user.id]}
                    cohorts={user.user_cohorts ?? []}
                    language={language}
                  />
                </React.Fragment>
              </PermissionComponent>
              <PermissionComponent neededPermission={[{ methods: ["DELETE"], route: "/users/*/proxmox/labs" }]}>
                <DstButton
                  variant="danger"
                  value={translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.FREE_LABS.BUTTON")}
                  btnImageBefore={<span className="dst-icon-server-04 me-2 align-text-bottom" />}
                  btnSize="sm"
                  btnClass="me-2"
                  loading={loadingFreeProxmoxLabs}
                  disabled={loadingFreeProxmoxLabs}
                  clickFunction={() => setShowConfirmFreeProxmoxLabs(true)}
                />
              </PermissionComponent>
              {transactions.length > 0 && (
                <React.Fragment>
                  {transactions.length === 1 ? (
                    <a
                      href={`${process.env.REACT_APP_HUBSPOT_DEAL_URL}/${transactions[0]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Hubspot height="28px" classes="ms-3 clickable" />
                    </a>
                  ) : (
                    <span onClick={() => setShowPopup(true)}>
                      <Hubspot height="28px" classes="ms-3 clickable" />
                    </span>
                  )}
                  <Popup
                    show={showPopup}
                    onClose={() => setShowPopup(false)}
                    modalTitle={translate(language, "PAGES.USER.USER_BLOCK.HUBSPOT_TRANSACTIONS")}
                    scrollable={true}
                  >
                    <div className="text-center">
                      {transactions.map((transactionId: any, index: number) => (
                        <DstButton
                          key={`transaction-${index}`}
                          btnClass="mx-2"
                          backgroundColor="var(--color-pumpkin)"
                          color="var(--color-white)"
                          variant="light"
                          value={transactionId}
                          btnImageAfter={<span className="dst-icon-link-external-01 ms-2" />}
                          clickFunction={() => {
                            window.open(`${process.env.REACT_APP_HUBSPOT_DEAL_URL}/${transactionId}`, "_blank");
                          }}
                        />
                      ))}
                    </div>
                  </Popup>
                </React.Fragment>
              )}
              {hasApprenticeshipFlag &&
                (!loadedDocuments ? (
                  <Placeholder animation="glow" className="me-2">
                    <Placeholder xs={3} style={{ borderRadius: "4px", height: "30px" }} />
                  </Placeholder>
                ) : (
                  <PermissionComponent
                    neededPermission={
                      !canGenerateStudentCardFlag
                        ? [{ methods: ["POST"], route: "/users/*/documents/regenerate/student-cards" }]
                        : [{ methods: ["POST"], route: "/users/*/documents/student-cards" }]
                    }
                  >
                    <React.Fragment>
                      <DstButton
                        variant="secondary"
                        value={translate(
                          language,
                          !canGenerateStudentCardFlag
                            ? "PAGES.USER.USER_BLOCK.REGENERATE_STUDENT_CARD"
                            : "PAGES.USER.USER_BLOCK.GENERATE_STUDENT_CARD"
                        )}
                        btnImageBefore={<span className="dst-icon-passport pe-2 align-text-bottom" />}
                        btnSize="sm"
                        btnClass="me-2"
                        clickFunction={() => {
                          setShowModalStudentCard(true);
                          if (!canGenerateStudentCardFlag) {
                            setRegenerateStudentCardPictureSource("previous");
                          } else {
                            setUseProfilePictureForStudentCard(true);
                          }
                          setStudentCardImage(null);
                        }}
                      />
                      <Popup
                        show={showModalStudentCard}
                        onClose={() => {
                          if (loadingStudentCard) return;
                          setShowModalStudentCard(false);
                          setUseProfilePictureForStudentCard(true);
                          setRegenerateStudentCardPictureSource("previous");
                          setStudentCardImage(null);
                        }}
                        modalTitle={translate(
                          language,
                          !canGenerateStudentCardFlag
                            ? "PAGES.USER.USER_BLOCK.STUDENT_CARD.REGENERATE.MODAL_TITLE"
                            : "PAGES.USER.USER_BLOCK.STUDENT_CARD.GENERATE.MODAL_TITLE"
                        )}
                      >
                        <div className="m-regular mb-1">
                          <FormLabel htmlFor="student-card-birthdate" className="m-regular mb-1">
                            {translate(language, "PAGES.USER.USER_BLOCK.STUDENT_CARD.BIRTHDATE_LABEL")}
                          </FormLabel>
                        </div>
                        <FormControl
                          id="student-card-birthdate"
                          type="date"
                          className="mb-3"
                          value={studentCardBirthdate}
                          min="1900-01-01"
                          max={new Date().toISOString().slice(0, 10)}
                          onChange={(event) => setStudentCardBirthdate(event.target.value)}
                        />
                        <div className="m-regular my-2">
                          {translate(language, "PAGES.USER.USER_BLOCK.STUDENT_CARD.IMAGE")}
                        </div>
                        <div className="mb-3">
                          {!canGenerateStudentCardFlag ? (
                            <React.Fragment>
                              <FormCheck
                                type="radio"
                                id="regenerate-student-card-picture-source-previous"
                                name="regenerate-student-card-picture-source"
                                className="mb-1"
                                checked={regenerateStudentCardPictureSource === "previous"}
                                label={translate(
                                  language,
                                  "PAGES.USER.USER_BLOCK.STUDENT_CARD.REGENERATE.USE_PREVIOUS_PICTURE"
                                )}
                                onChange={() => {
                                  setRegenerateStudentCardPictureSource("previous");
                                  setStudentCardImage(null);
                                }}
                              />
                              <FormCheck
                                type="radio"
                                id="regenerate-student-card-picture-source-profile"
                                name="regenerate-student-card-picture-source"
                                className="mb-1"
                                checked={regenerateStudentCardPictureSource === "profile"}
                                label={translate(
                                  language,
                                  "PAGES.USER.USER_BLOCK.STUDENT_CARD.REGENERATE.USE_PROFILE_PICTURE"
                                )}
                                onChange={() => {
                                  setRegenerateStudentCardPictureSource("profile");
                                  setStudentCardImage(null);
                                }}
                              />
                              <FormCheck
                                type="radio"
                                id="regenerate-student-card-picture-source-upload"
                                name="regenerate-student-card-picture-source"
                                checked={regenerateStudentCardPictureSource === "upload"}
                                label={translate(
                                  language,
                                  "PAGES.USER.USER_BLOCK.STUDENT_CARD.REGENERATE.UPLOAD_NEW_PICTURE"
                                )}
                                onChange={() => setRegenerateStudentCardPictureSource("upload")}
                              />
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              <FormCheck
                                type="radio"
                                id="student-card-picture-source-profile"
                                name="student-card-picture-source"
                                className="mb-1"
                                checked={useProfilePictureForStudentCard}
                                label={translate(
                                  language,
                                  "PAGES.USER.USER_BLOCK.STUDENT_CARD.GENERATE.USE_PROFILE_PICTURE"
                                )}
                                onChange={() => {
                                  setUseProfilePictureForStudentCard(true);
                                  setStudentCardImage(null);
                                }}
                              />
                              <FormCheck
                                type="radio"
                                id="student-card-picture-source-upload"
                                name="student-card-picture-source"
                                checked={!useProfilePictureForStudentCard}
                                label={translate(
                                  language,
                                  "PAGES.USER.USER_BLOCK.STUDENT_CARD.GENERATE.UPLOAD_NEW_PICTURE"
                                )}
                                onChange={() => setUseProfilePictureForStudentCard(false)}
                              />
                            </React.Fragment>
                          )}
                        </div>
                        {!canGenerateStudentCardFlag ? (
                          regenerateStudentCardPictureSource === "upload" ? (
                            <React.Fragment>
                              <p className="xs-regular dark-gray-font">
                                {translate(
                                  language,
                                  "PAGES.USER.USER_BLOCK.STUDENT_CARD.REGENERATE.IMAGE_REQUIREMENTS"
                                )}
                              </p>
                              <FormControl
                                type="file"
                                className="mb-3"
                                accept="image/png,image/jpeg"
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  setStudentCardImage(event.target.files?.[0] ?? null)
                                }
                              />
                            </React.Fragment>
                          ) : (
                            <React.Fragment></React.Fragment>
                          )
                        ) : !useProfilePictureForStudentCard ? (
                          <React.Fragment>
                            <p className="xs-regular dark-gray-font">
                              {translate(language, "PAGES.USER.USER_BLOCK.STUDENT_CARD.GENERATE.IMAGE_REQUIREMENTS")}
                            </p>
                            <FormControl
                              type="file"
                              className="mb-3"
                              accept="image/png,image/jpeg"
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setStudentCardImage(event.target.files?.[0] ?? null)
                              }
                            />
                          </React.Fragment>
                        ) : (
                          <React.Fragment></React.Fragment>
                        )}
                        {!canGenerateStudentCardFlag && regenerateStudentCardPictureSource === "previous" ? (
                          <p className="m-regular dark-gray-font text-center mb-3">
                            {translate(language, "PAGES.USER.USER_BLOCK.STUDENT_CARD.REGENERATE.PREVIOUS_PICTURE_INFO")}
                          </p>
                        ) : (
                          <React.Fragment>
                            <div className="s-regular mb-2">
                              {translate(language, "PAGES.USER.USER_BLOCK.STUDENT_CARD.PREVIEW")}
                            </div>
                            {(
                              !canGenerateStudentCardFlag
                                ? regenerateStudentCardSelectedPreviewUrl
                                : studentCardSelectedPreviewUrl
                            ) ? (
                              <div className="text-center mb-3">
                                <img
                                  id="student-card-preview-image"
                                  src={
                                    !canGenerateStudentCardFlag
                                      ? regenerateStudentCardSelectedPreviewUrl!
                                      : studentCardSelectedPreviewUrl!
                                  }
                                  alt={translate(language, "PAGES.USER.USER_BLOCK.STUDENT_CARD.PREVIEW")}
                                  className={
                                    !canGenerateStudentCardFlag
                                      ? regenerateStudentCardPictureSource === "profile"
                                        ? "profile"
                                        : undefined
                                      : useProfilePictureForStudentCard
                                      ? "profile"
                                      : undefined
                                  }
                                />
                              </div>
                            ) : (
                              <div className="text-center text-muted mb-3">
                                {translate(language, "PAGES.USER.USER_BLOCK.STUDENT_CARD.NO_PREVIEW")}
                              </div>
                            )}
                          </React.Fragment>
                        )}
                        <i className="xs-regular dark-gray-font">
                          {translate(
                            language,
                            !canGenerateStudentCardFlag
                              ? "PAGES.USER.USER_BLOCK.STUDENT_CARD.REGENERATE.NOTE"
                              : "PAGES.USER.USER_BLOCK.STUDENT_CARD.GENERATE.NOTE"
                          )}
                        </i>
                        <DstButton
                          value={translate(
                            language,
                            !canGenerateStudentCardFlag
                              ? "PAGES.USER.USER_BLOCK.STUDENT_CARD.REGENERATE.SUBMIT"
                              : "PAGES.USER.USER_BLOCK.STUDENT_CARD.GENERATE.SUBMIT"
                          )}
                          btnClass="ms-auto d-block"
                          btnSize="lg"
                          loading={loadingStudentCard}
                          disabled={
                            !studentCardBirthdate ||
                            loadingStudentCard ||
                            (!canGenerateStudentCardFlag
                              ? regenerateStudentCardPictureSource === "upload" && !studentCardImage
                              : !useProfilePictureForStudentCard && !studentCardImage)
                          }
                          clickFunction={() => {
                            const onSuccess = () => {
                              setShowModalStudentCard(false);
                              setStudentCardBirthdate("");
                              setUseProfilePictureForStudentCard(true);
                              setRegenerateStudentCardPictureSource("previous");
                              setStudentCardImage(null);
                              setStudentCardSuccessToast(!canGenerateStudentCardFlag ? "regenerate" : "generate");
                              refreshUserDocuments?.();
                            };

                            if (!canGenerateStudentCardFlag) {
                              regenerateStudentCard(
                                language,
                                user.id,
                                studentCardBirthdate,
                                regenerateStudentCardPictureSource === "upload" ? studentCardImage : null,
                                regenerateStudentCardPictureSource === "previous",
                                setLoadingStudentCard,
                                onSuccess
                              );
                              return;
                            }

                            generateStudentCard(
                              language,
                              user.id,
                              studentCardBirthdate,
                              useProfilePictureForStudentCard ? null : studentCardImage,
                              setLoadingStudentCard,
                              onSuccess
                            );
                          }}
                        />
                      </Popup>
                    </React.Fragment>
                  </PermissionComponent>
                ))}
            </div>
            <hr className="my-4" />
            <div className="d-flex flex-column gap-2">
              <div className="d-flex flex-wrap gap-2">
                <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/auth/login-as/*" }]}>
                  <DstButton
                    clickFunction={() => loginAsFunction?.()}
                    value={translate(language, "PAGES.USER.USER_BLOCK.LOG_IN_AS")}
                    btnImageBefore={<LogIn />}
                  />
                </PermissionComponent>
                <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/admin/auth/logout-user" }]}>
                  <DstButton
                    clickFunction={() => logoutFunction?.()}
                    value={translate(language, "PAGES.USER.USER_BLOCK.LOG_OUT_USER")}
                    btnImageBefore={<LogOut />}
                    variant="danger"
                  />
                </PermissionComponent>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/users/*/resend-credentials" }]}>
                  <DstButton
                    variant="secondary"
                    clickFunction={() => setShowConfirmResendCredentials(true)}
                    value={translate(language, "PAGES.USER.USER_BLOCK.RESEND_CREDENTIALS")}
                    btnImageBefore={<span className="dst-icon-send-03 me-2 align-text-bottom" />}
                    loading={loadingResendCredentials}
                    disabled={loadingResendCredentials}
                  />
                </PermissionComponent>
                <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/users/merge" }]}>
                  <DstButton
                    variant="warning"
                    value={translate(language, "PAGES.USER.USER_BLOCK.MERGE.BUTTON")}
                    btnImageBefore={<span className="dst-icon-users-right me-2 align-text-bottom" />}
                    loading={loadingMerge}
                    clickFunction={() => setShowMergeUserModal(true)}
                  />
                </PermissionComponent>
              </div>
            </div>
            <Popup
              show={showMergeUserModal}
              onClose={() => setShowMergeUserModal(false)}
              modalTitle={translate(language, "PAGES.USER.USER_BLOCK.MERGE.MODAL.TITLE")}
            >
              <p
                className="m-regular"
                dangerouslySetInnerHTML={{
                  __html: translate(language, "PAGES.USER.USER_BLOCK.MERGE.MODAL.TEXT"),
                }}
              />
              <MultiSelectLiveDropdown
                category={["users"]}
                label={
                  mergeTargetUser && mergeTargetUser.length > 0
                    ? `${mergeTargetUser.length} ${translate(
                        language,
                        "PAGES.USER.USER_BLOCK.MERGE.MODAL.SELECTED_USERS"
                      )}`
                    : translate(language, "PAGES.USER.USER_BLOCK.MERGE.MODAL.SELECT_USER")
                }
                language={language}
                mockClass="rounded-0 rounded-end"
                toggleClass="min-w-100px m-regular"
                onConfirm={(selectedUsers: any[]) => setMergeTargetUser(selectedUsers.map((user) => user.value))}
                showFilterButton={false}
                clearItemsOnSend={false}
                isSingleSelect
              />
              <DstButton
                value={translate(language, "PAGES.USER.USER_BLOCK.MERGE.MODAL.BUTTON")}
                btnClass="mx-auto d-block"
                variant="danger"
                disabled={!mergeTargetUser || mergeTargetUser.length === 0 || loadingMerge}
                loading={loadingMerge}
                clickFunction={() => setShowConfirmMerge(true)}
              />
            </Popup>
            <DstConfirm
              show={showConfirmResendCredentials}
              onHide={() => !loadingResendCredentials && setShowConfirmResendCredentials(false)}
              onConfirm={() => {
                setShowConfirmResendCredentials(false);
                resendCredentials(user.id, language, setLoadingResendCredentials, () =>
                  setShowResendCredentialsToast(true)
                );
              }}
              title={translate(language, "PAGES.USER.USER_BLOCK.RESEND_CREDENTIALS_CONFIRM.TITLE")}
              messageExplain={translate(language, "PAGES.USER.USER_BLOCK.RESEND_CREDENTIALS_CONFIRM.EXPLAIN")}
              messageConfirm={translate(language, "PAGES.USER.USER_BLOCK.RESEND_CREDENTIALS_CONFIRM.MESSAGE")}
              confirmTextButton={translate(language, "PAGES.USER.USER_BLOCK.RESEND_CREDENTIALS_CONFIRM.BUTTON")}
              icon="dst-icon-send-03"
              iconColor="orange"
              iconBackgroundColor="var(--color-light-orange)"
            />
            <DstConfirm
              show={showConfirmFreeProxmoxLabs}
              onHide={() => !loadingFreeProxmoxLabs && setShowConfirmFreeProxmoxLabs(false)}
              onConfirm={() => {
                setShowConfirmFreeProxmoxLabs(false);
                freeProxmoxLabs(
                  user.id,
                  language,
                  setLoadingFreeProxmoxLabs,
                  (labs: any) => setFreeProxmoxLabsCount(labs.length),
                  showAlert
                );
              }}
              title={translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.FREE_LABS.CONFIRM.TITLE")}
              messageExplain={translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.FREE_LABS.CONFIRM.EXPLAIN").replace(
                "{{USER}}",
                `${user.first_name} ${user.last_name}`
              )}
              messageConfirm={translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.FREE_LABS.CONFIRM.MESSAGE")}
              confirmTextButton={translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.FREE_LABS.CONFIRM.BUTTON")}
              icon="dst-icon-server-04"
              iconColor="red"
              iconBackgroundColor="var(--color-light-pink)"
            />
            <DstConfirm
              show={showConfirmMerge}
              onHide={() => !loadingMerge && setShowConfirmMerge(false)}
              onConfirm={() => {
                setLoadingMerge(true);
                mergeSelectedUser(mergeTargetUser[0]?.id);
                setShowConfirmMerge(false);
              }}
              title={translate(language, "PAGES.USER.USER_BLOCK.MERGE.CONFIRM.TITLE")}
              messageExplain={
                mergeTargetUser &&
                translate(language, "PAGES.USER.USER_BLOCK.MERGE.CONFIRM.EXPLAIN")
                  .replace("{{USER_1}}", `${user.first_name} ${user.last_name} (ID: ${user.id})`)
                  .replace(
                    "{{USER_2}}",
                    `${mergeTargetUser[0]?.first_name} ${mergeTargetUser[0]?.last_name} (ID: ${mergeTargetUser[0]?.id})`
                  )
              }
              messageConfirm={translate(language, "PAGES.USER.USER_BLOCK.MERGE.CONFIRM.MESSAGE")}
              confirmTextButton={translate(language, "PAGES.USER.USER_BLOCK.MERGE.CONFIRM.BUTTON_CONFIRM")}
              icon="dst-icon-git-merge"
              iconColor="red"
              iconSize="50px"
              iconBackgroundColor="var(--color-light-pink)"
            />
            {createPortal(
              <React.Fragment>
                <ToastContainer position="top-end" className="p-3">
                  <Toast
                    bg="success"
                    show={showCopyShortcutToast}
                    onClose={() => setShowCopyShortcutToast(false)}
                    delay={3000}
                    autohide
                  >
                    <Toast.Body className="text-white">
                      {translate(language, "PAGES.USER.USER_BLOCK.COPY_SHORTCUT_SUCCESS")}
                    </Toast.Body>
                  </Toast>
                  <Toast
                    bg="success"
                    show={showResendCredentialsToast}
                    onClose={() => setShowResendCredentialsToast(false)}
                    delay={3000}
                    autohide
                  >
                    <Toast.Body className="text-white">
                      {translate(language, "PAGES.USER.USER_BLOCK.RESEND_CREDENTIALS_SUCCESS")}
                    </Toast.Body>
                  </Toast>
                  <Toast
                    bg={proxmoxDeployedCount ? "success" : "warning"}
                    show={proxmoxDeployedCount !== null}
                    onClose={() => setProxmoxDeployedCount(null)}
                    delay={3000}
                    autohide
                  >
                    <Toast.Body className="text-white">
                      {translate(
                        language,
                        proxmoxDeployedCount
                          ? "PAGES.USER.USER_BLOCK.PROXMOX.SUCCESS"
                          : "PAGES.USER.USER_BLOCK.PROXMOX.ALREADY_EXISTS"
                      )}
                    </Toast.Body>
                  </Toast>
                  <Toast
                    bg="success"
                    show={freeProxmoxLabsCount !== null}
                    onClose={() => setFreeProxmoxLabsCount(null)}
                    delay={3000}
                    autohide
                  >
                    <Toast.Body className="text-white">
                      {translate(
                        language,
                        freeProxmoxLabsCount
                          ? "PAGES.USER.USER_BLOCK.PROXMOX.FREE_LABS.SUCCESS"
                          : "PAGES.USER.USER_BLOCK.PROXMOX.FREE_LABS.NO_LAB"
                      ).replace("{{COUNT}}", String(freeProxmoxLabsCount))}
                    </Toast.Body>
                  </Toast>
                </ToastContainer>
                <ToastContainer position="top-center" className="p-3">
                  <Toast
                    bg="success"
                    show={studentCardSuccessToast !== null}
                    onClose={() => setStudentCardSuccessToast(null)}
                    delay={3000}
                    autohide
                  >
                    <Toast.Body className="text-white">
                      {translate(
                        language,
                        studentCardSuccessToast === "regenerate"
                          ? "PAGES.USER.USER_BLOCK.STUDENT_CARD.REGENERATE.SUCCESS"
                          : "PAGES.USER.USER_BLOCK.STUDENT_CARD.GENERATE.SUCCESS"
                      )}
                    </Toast.Body>
                  </Toast>
                </ToastContainer>
              </React.Fragment>,
              document.body
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Placeholder animation="glow">
              <div className="my-3">
                <Placeholder as="div" xs={4} />
              </div>
            </Placeholder>
            <div className="d-flex flex-wrap row-gap-2">
              <DstButton mock btnClass="me-3" btnWidth="66px" />
              <DstButton mock btnClass="me-3" btnWidth="102px" />
              <DstButton mock btnClass="me-3" btnWidth="120px" />
            </div>

            <hr className="my-4" />
            <div className="d-flex flex-column gap-2">
              <div className="d-flex flex-wrap gap-2">
                <DstButton mock btnWidth="92px" />
                <DstButton mock btnWidth="112px" />
              </div>
              <div className="d-flex flex-wrap gap-2">
                <DstButton mock btnWidth="140px" />
                <DstButton mock btnWidth="180px" />
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default UserBlock;
